<script setup lang="ts">
/**
 * The documents sidebar's two toolbar rows (MAR-948 — split out of `FileExplorer.vue`).
 *
 * TWO ROWS (AlexAmin/marcus#158). Row 1 is search plus "+ Nuevo": search is the thing people reach
 * for, and it should not share its row with three other controls. Row 2 is the filtering and
 * sorting pair. The rail and /app/docs are laid out the same way, from the same component for the
 * source picker.
 */
import { Plus } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { useDocumentsStore } from '~/stores/documents'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu'
import SearchInput from '~/components/controls/SearchInput.vue'
import SortMenu from '~/components/controls/SortMenu.vue'
import type { ExplorerCreateAction } from '~/composables/documents/useExplorerCreateActions'

defineProps<{
  createActions: ExplorerCreateAction[]
  /** "New folder" puts focus in its name input; the menu must not pull it back to the trigger. */
  keepFocusOnClose: boolean
}>()

const search = defineModel<string>('search', { required: true })
const newMenuOpen = defineModel<boolean>('newMenuOpen', { default: false })

const emit = defineEmits<{
  input: []
  clear: []
  'run-action': [action: ExplorerCreateAction]
  'sources-changed': []
}>()

const { t } = useI18n()
const documents = useDocumentsStore()
const { sortField, sortOrder } = storeToRefs(documents)

const sortFields = computed(() => [
  { field: 'name' as const, label: t('common.sortName') },
  { field: 'created' as const, label: t('common.sortUploadDate') },
  { field: 'date' as const, label: t('common.sortModifiedDate') },
])
</script>

<template>
  <!-- Row 1 — search + New -->
  <div class="px-3 pt-2 shrink-0 flex items-center gap-2">
    <SearchInput v-model="search" size="xs" class="flex-1" @input="emit('input')" @clear="emit('clear')" />

    <DropdownMenu v-model:open="newMenuOpen">
      <DropdownMenuTrigger as-child>
        <button
          type="button"
          class="h-7 px-2.5 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent-lighter transition-colors flex items-center gap-1"
        >
          <Plus class="h-3.5 w-3.5" />
          {{ t('common.new') }}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="min-w-[160px]" @close-auto-focus="(e: Event) => keepFocusOnClose && e.preventDefault()">
        <DropdownMenuItem
          v-for="action in createActions"
          :key="action.key"
          class="text-xs"
          @select="emit('run-action', action)"
        >
          <component :is="action.icon" class="h-3.5 w-3.5 text-muted-foreground" />
          {{ action.label }}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <!-- Row 2 — source filter + sort -->
  <div class="px-3 py-2 shrink-0 flex items-center gap-2">
    <!-- `min-w-0` so the trigger's own `truncate` can act: without it a flex child refuses to shrink
         below its content and the label pushes the sort button out of the rail, which is the
         clipping this row exists to end. -->
    <DocumentsDocumentSourceFilter size="xs" class="min-w-0 flex-1" @change="emit('sources-changed')" />

    <SortMenu :options="sortFields" :field="sortField" :order="sortOrder" size="xs" @sort="documents.setSort" />
  </div>
</template>
